<template>
    <button type="button"
        ref="rootRef"
        :disabled="props.disabled"
        :class="[props.class, props.loading ? 'loader' : '', props.icon && !props.name ? 'icon-mode' : '', props.errorsTooltip ? 'errors-tooltip' : '']"

        v-tooltip.bottom.right="props.errorsTooltip"
        @click="click"
    >
        <div class="collage-box" v-if="slots.collage">
            <slot name="collage"></slot>
        </div>

        <Icon :icon="props.icon" v-if="props.icon" />

        <span v-if="props.name">
            {{ props.name }}
        </span>

        <slot></slot>

        <template v-if="props.kebabTrigger">
            <Icon icon="chevron-down" class="kebab-trigger"/>
        </template>

        <Icon
            v-if="props.errorsTooltip"
            icon="exclamation"
            class="errors-tooltip"
        />

        <Loader :inside="true" :class="props.class" v-if="props.loading" />
    </button>
</template>

<script setup>
import { useSlots, ref } from 'vue';
import Icon from '🔗/components/ui/Icon.vue';
import Loader from '🔗/components/ui/Loader.vue';
import stores from '@/stores';

const others = stores.others();

const rootRef = ref(null);
const slots = useSlots();

const props = defineProps({
    icon: { type: String, default: '' },
    name: { type: String, default: '' },
    class: { type: String, default: '' },
    errorsTooltip: { type: [String, null], default: null },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    kebabTrigger: { type: Boolean, default: false }
});

const click = () => {
    if (props.disabled) {
        return;
    }

    others.vibrate();
}

defineExpose({
    el: rootRef
});
</script>

<style lang="scss" scoped>
button {
    cursor: pointer;
    border: none;
    outline: none;
    gap: 12px;
    height: 56px;
    display: flex;
    font-variant: unset;
    transition: all 0.4s;
    align-items: center;
    justify-content: center;
    background: var(--gradient-2);
    padding: 0 24px;
    border-radius: 10px;

    font: var(--font-body-1);
    color: var(--white-500);
    transition: all 0.2s;

    svg {
        fill: var(--white-500);
        stroke: var(--white-80);
        width: 18px;
        height: 18px;
    }

    &:before {
        border-radius: 10px;
    }

    &[disabled] {
        cursor: default;
        filter: grayscale(1);
        opacity: .4;
        &.loader {
            opacity: 1;
            filter: none;
        }

    }

    &.reverse {
        flex-direction: row-reverse;
    }

    &.size-l {
        height: 50px;
        svg {
            width: 16px;
            height: 16px;
        }
    }

    &.size-m {
        height: 38px;
        svg {
            width: 14px;
            height: 14px;
        }
    }

    &.size-s {
        padding: 0 16px;
        height: 32px;
        min-width: 54px;
        border-radius: 16px;
        font: var(--font-caption);
        flex-shrink: 0;

        &:before {
            border-radius: 16px;
        }

        // & > span {
        //     padding-top: 2px;
        // }

        svg {
            width: 16px;
            height: 16px;
        }
    }

    &.secondary {
        background-color: transparent;
        color: var(--dark-500);
        position: relative;
        background: var(--gradient-2) border-box;
        font-weight: 500;

        &:before {
            content: "";
            position: absolute;
            left: 1px;
            top: 1px;
            right: 1px;
            background: var(--white-500);
            bottom: 1px;
        }

        svg  {
            fill: var(--primary-500);
            stroke: var(--tertiary-500);
            z-index: 1;
        }

        span {
            z-index: 1;
            @include gradient-color;
        }
    }

    &.tertiary {
        background: transparent;
        svg {
            fill: var(--primary-500);
            stroke: var(--tertiary-500);
        }

        span {
            color: var(--primary-500);
        }

        &.submenu {
            svg {
                fill: var(--gray-500);
                stroke: var(--dark-80);
            }

            span {
                color: var(--gray-80);
            }
        }

        &.invert {
            svg {
                fill: var(--white-500);
                stroke: var(--white-80);
            }

            span {
                color: var(--white-500);
            }
        }

        &.gray {
            svg {
                fill: var(--gray-500);
                stroke: var(--gray-80);
            }

            span {
                color: var(--gray-500);
            }
        }

        &.link {
            padding: 0;
            height: auto;
            min-width: auto;
        }

        &.active {
            background: var(--light-10);
            backdrop-filter: blur(2px);
            -webkit-backdrop-filter: blur(2px);
            svg {
                fill: var(--primary-500);
                stroke: var(--tertiary-500);
            }

            &.invert {
                svg {
                    fill: var(--white-500);
                    stroke: var(--white-80);
                }
            }
        }
    }

    &.menu {
        border-radius: 24px;
        background: transparent;

        svg {
            fill: var(--dark-500);
            stroke: var(--dark-80);
        }

        span {
            font: var(--font-body-2);
            color: var(--dark-500);
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
        }

        &.active {
            background: var(--light-80);
        }
    }

    &.tap {
        padding: 0 8px 0 0;
        background: transparent;
        position: relative;
        justify-content: start;

        .collage-box {
            width: 56px;
            height: 56px;
            flex-shrink: 0;
            z-index: 1;
            padding: 4px;
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;

            & + svg {
                opacity: 0;
            }
            & + span {
                margin-left: 69px;
            }
        }

        svg {
            fill: var(--dark-500);
            stroke: var(--dark-80);
            height: 30px;
            width: 30px;
            z-index: 1;
            margin: 0 13px;
            flex-shrink: 0;
        }

        &:before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            border-radius: 10px;
            width: 56px;
            background: var(--light-80);
        }

        span {
            font: var(--font-body-2);
            font-weight: bold;
            color: var(--dark-500);
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
        }
    }

    &.errors-tooltip {
        position: relative;
        & > * {
            opacity: 0;
        }

        .errors-tooltip {
            opacity: 1;
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translateY(-50%) translateX(-50%);
            width: 16px;
            height: 16px;
            fill: var(--secondary-700);
        }
    }

    &.loader {
        background-color: transparent;
        position: relative;
        overflow: hidden;
        min-width: 52px;

        & > * {
            opacity: 0;
        }

        & > .inside {
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            opacity: 1;
            transform: translateY(18px);
        }

        &.size-s {
            & > .inside {
                transform: translateY(0);
            }
        }

        &.size-m {
            & > .inside {
                transform: translateY(6px);
            }
        }

        &.size-l {
            & > .inside {
                transform: translateY(12px);
            }
        }
    }

    &.icon-mode {
        padding: 0;
        width: 56px;

        &.size-l {
            width: 50px;
            height: 50px;
        }

        &.size-m {
            width: 38px;
            height: 38px;
        }

        &.size-s {
            width: 32px;
            height: 32px;
        }
    }


    .kebab-trigger {
        transition: all .4s;
    }

    &.active {
        .kebab-trigger {
            transform: rotate(180deg);
        }
    }
}

@media (hover: hover) and (pointer: fine) {
    button {
        &:hover {
            opacity: .8;
        }
        &.tertiary {
            &.submenu {
                &:hover {
                    svg {
                        fill: var(--dark-90);
                        stroke: var(--dark-80);
                    }

                    span {
                        color: var(--dark-90);
                    }   
                }
            }
            &.gray {
                &:hover {
                    svg {
                        fill: var(--dark-500);
                        stroke: var(--dark-80);
                    }

                    span {
                        color: var(--dark-500);
                    }
                }

                &.invert {
                    &:hover {
                        background: transparent;
                        svg {
                            fill: var(--white-500);
                            stroke: var(--white-80);
                        }

                        span {
                            color: var(--white-500);
                        } 
                    }
                }

            }

            &:hover {
                // svg {
                //     fill: var(--primary-500);
                //     stroke: var(--tertiary-500);
                // }

                // span {
                //     color: var(--primary-500);
                // }
                &.invert {
                    background: var(--white-20);
                    svg {
                        fill: var(--white-500);
                        stroke: var(--white-80);
                    }

                    span {
                        color: var(--white-500);
                    }
                }
            }
        }

        &.menu {
            &:hover {
                background: var(--light-20);
            }
        }
    }
}

.direction-rtl button {
    &.tap {
        padding: 0 0 0 8px;
        &:before {
            left: auto;
            right: 0;
        }
    }
}
</style>
